import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Section, Eyebrow, Headline, Lead } from '../../components/iss/IssPrimitives';
import FileDrop from '../../components/FileDrop';
import { EVALUATION_TEMPLATE_LINKS, IT_DEMAND_FORM } from './evaluationLinks';
import { documentLinkProps, isPlaceholderLink } from '../templateFile';
import {
  buildOutcome,
  isCriteriaComplete,
  isEvaluationComplete,
  mapApiCriteriaToResults,
  type SolutionType,
  type SuccessCriterionResult,
} from './evaluationOutcome';
import apiService from '../../service/ApiService';
import '../initiation/PocTypePage.scss';

const FILE_ACCEPT =
  '.pdf,.doc,.docx,.ppt,.pptx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation';

type LocationState = {
  pocId?: string;
  successCriteria?: { criterion: string; measurement: string }[];
  evaluation?: {
    criteriaResults?: SuccessCriterionResult[];
    evaluationReportFileName?: string | null;
    warmUpMessage?: string;
    recapMeetingNotes?: string;
    proceedWithImplementation?: boolean;
    outcomeComment?: string;
    solutionType?: SolutionType;
  };
  [key: string]: unknown;
};

const EvaluationPhasePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as LocationState | null) ?? {};
  const pocId = state.pocId;

  const [criteriaResults, setCriteriaResults] = useState<
    SuccessCriterionResult[]
  >([]);
  const [criteriaLoading, setCriteriaLoading] = useState(false);
  const [criteriaLoadError, setCriteriaLoadError] = useState<string | null>(
    null
  );

  const [evaluationReportFile, setEvaluationReportFile] = useState<File | null>(
    null
  );
  const [evaluationReportFileName, setEvaluationReportFileName] = useState<
    string | null
  >(state.evaluation?.evaluationReportFileName ?? null);
  const [warmUpMessage, setWarmUpMessage] = useState(
    state.evaluation?.warmUpMessage ?? ''
  );
  const [recapMeetingNotes, setRecapMeetingNotes] = useState(
    state.evaluation?.recapMeetingNotes ?? ''
  );
  const [proceedWithImplementation, setProceedWithImplementation] = useState<
    boolean | null
  >(
    typeof state.evaluation?.proceedWithImplementation === 'boolean'
      ? state.evaluation.proceedWithImplementation
      : null
  );
  const [outcomeComment, setOutcomeComment] = useState(
    state.evaluation?.outcomeComment ?? ''
  );
  const [solutionType, setSolutionType] = useState<SolutionType | null>(
    state.evaluation?.solutionType ?? null
  );
  const [attemptedComplete, setAttemptedComplete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!pocId) {
      setCriteriaLoadError('Missing PoC id. Complete earlier phases first.');
      return;
    }

    let cancelled = false;
    (async () => {
      setCriteriaLoading(true);
      setCriteriaLoadError(null);
      try {
        const rows = await apiService.getSuccessCriteria(pocId);
        if (!cancelled) {
          setCriteriaResults(mapApiCriteriaToResults(rows));
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setCriteriaLoadError(
            'Could not load success criteria. Check Network tab (PoC must exist).'
          );
        }
      } finally {
        if (!cancelled) setCriteriaLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pocId]);

  const canComplete =
    isEvaluationComplete({
      criteriaResults,
      evaluationReportFileName,
      proceedWithImplementation,
      solutionType,
    }) && evaluationReportFile !== null;

  const outcomePreview = useMemo(() => {
    if (proceedWithImplementation === null) return null;
    return buildOutcome(
      proceedWithImplementation,
      outcomeComment,
      solutionType
    );
  }, [proceedWithImplementation, outcomeComment, solutionType]);

  const updateCriterion = (
    index: number,
    field: keyof Pick<
      SuccessCriterionResult,
      'wasItMet' | 'howMeasured' | 'outcomeDescription'
    >,
    value: string
  ) => {
    setCriteriaResults((current) =>
      current.map((row, i) => {
        if (i !== index) return row;
        if (field === 'wasItMet') {
          return {
            ...row,
            wasItMet: value === 'Yes' || value === 'No' ? value : '',
          };
        }
        return { ...row, [field]: value };
      })
    );
  };

  const handleComplete = async () => {
    setAttemptedComplete(true);
    setSubmitError(null);
    if (!canComplete || proceedWithImplementation === null) return;

    if (!pocId) {
      setSubmitError('Missing PoC id.');
      return;
    }
    if (!evaluationReportFile) {
      setSubmitError('Choose an evaluation report file.');
      return;
    }

    const outcome = buildOutcome(
      proceedWithImplementation,
      outcomeComment,
      solutionType
    );

    setIsSubmitting(true);
    try {
      await apiService.uploadDocument(
        pocId,
        'EVALUATION_REPORT',
        evaluationReportFile
      );

      for (const row of criteriaResults) {
        await apiService.saveCriterionEvaluation(pocId, row.id, {
          wasMet: row.wasItMet === 'Yes',
          howMeasured: row.howMeasured.trim(),
          resultDescription: row.outcomeDescription.trim(),
        });
      }

      const savedOutcome = await apiService.saveOutcome(pocId, {
        successful: outcome.successful,
        proceed: outcome.proceed,
        comment: outcome.comment.trim() || null,
        nextStepType: outcome.nextStepType,
      });

      navigate('/pocs/new/evaluation/complete', {
        state: {
          ...state,
          evaluation: {
            criteriaResults,
            evaluationReportFileName,
            warmUpMessage,
            recapMeetingNotes,
            proceedWithImplementation,
            outcomeComment,
            solutionType: solutionType ?? undefined,
            outcome: {
              ...outcome,
              nextStepType: savedOutcome.nextStepType,
            },
          },
        },
      });
    } catch (err) {
      console.error(err);
      setSubmitError(
        'Could not complete evaluation. PoC must be in Evaluation (IN_EVALUATION). Check Network tab.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="initiation">
      <aside className="initiation__phases" aria-label="PoC phases">
        <div className="initiation__phase initiation__phase--disabled">
          <span className="initiation__phase-num">1</span>
          <span>Initiation</span>
        </div>
        <div className="initiation__phase initiation__phase--disabled">
          <span className="initiation__phase-num">2</span>
          <span>Preparation</span>
        </div>
        <div className="initiation__phase initiation__phase--disabled">
          <span className="initiation__phase-num">3</span>
          <span>Running</span>
        </div>
        <div className="initiation__phase initiation__phase--active">
          <span className="initiation__phase-num">4</span>
          <span>Evaluation</span>
        </div>
      </aside>

      <div className="initiation__main">
        <Section theme="light" spacing="tight">
          <Eyebrow>POC Evaluation</Eyebrow>
          <Headline as="h1" size="3xl">
            Evaluation phase
          </Headline>
          <Lead>
            Review success criteria, upload your evaluation report, and record
            the final outcome. Results are saved to the backend when you
            complete this phase.
          </Lead>

          <div className="phase-gate">
            <p className="phase-gate__title">1. Success criteria check</p>
            {criteriaLoading ? (
              <p className="scorecard__notice" role="status">
                Loading success criteria…
              </p>
            ) : null}
            {criteriaLoadError ? (
              <p className="bj-field__error">{criteriaLoadError}</p>
            ) : null}
            {!criteriaLoading &&
            !criteriaLoadError &&
            criteriaResults.length === 0 ? (
              <p className="bj-field__error">
                No success criteria found. Complete Preparation success criteria
                first.
              </p>
            ) : null}
            {!criteriaLoading
              ? criteriaResults.map((row, index) => (
                  <div key={row.id} className="evaluation-criterion">
                    <p>
                      <strong>Criterion {index + 1}:</strong> {row.criterion}
                    </p>
                    <p className="scorecard__help-note">
                      How you planned to evaluate: {row.measurement}
                    </p>
                    <div className="bj-choice-list">
                      <span className="bj-field__label">Was it met?</span>
                      <label className="bj-choice">
                        <input
                          type="radio"
                          name={`was-met-${row.id}`}
                          checked={row.wasItMet === 'Yes'}
                          disabled={isSubmitting}
                          onChange={() =>
                            updateCriterion(index, 'wasItMet', 'Yes')
                          }
                        />
                        <span>Yes</span>
                      </label>
                      <label className="bj-choice">
                        <input
                          type="radio"
                          name={`was-met-${row.id}`}
                          checked={row.wasItMet === 'No'}
                          disabled={isSubmitting}
                          onChange={() =>
                            updateCriterion(index, 'wasItMet', 'No')
                          }
                        />
                        <span>No</span>
                      </label>
                    </div>
                    <label className="bj-field">
                      <span className="bj-field__label">
                        How was it measured?
                      </span>
                      <input
                        className="bj-field__input"
                        value={row.howMeasured}
                        disabled={isSubmitting}
                        onChange={(e) =>
                          updateCriterion(index, 'howMeasured', e.target.value)
                        }
                      />
                    </label>
                    <label className="bj-field">
                      <span className="bj-field__label">Description</span>
                      <textarea
                        className="bj-field__input bj-field__input--area"
                        value={row.outcomeDescription}
                        disabled={isSubmitting}
                        onChange={(e) =>
                          updateCriterion(
                            index,
                            'outcomeDescription',
                            e.target.value
                          )
                        }
                      />
                    </label>
                  </div>
                ))
              : null}
            {attemptedComplete && !isCriteriaComplete(criteriaResults) ? (
              <p className="bj-field__error">
                Fill in all result fields for each success criterion.
              </p>
            ) : null}
          </div>

          <div className="phase-gate">
            <p className="phase-gate__title">
              2. Prepare and upload evaluation report
            </p>
            <p>
              Upload your PoC Evaluation Report summarising outcomes, lessons
              learned, and recommendations.
            </p>
            <ul className="phase-gate__list">
              <li>
                <a
                  className="scorecard__link"
                  {...documentLinkProps(EVALUATION_TEMPLATE_LINKS.evaluationPptx.href)}
                >
                  {EVALUATION_TEMPLATE_LINKS.evaluationPptx.label}
                </a>
                {isPlaceholderLink(
                  EVALUATION_TEMPLATE_LINKS.evaluationPptx.href
                )
                  ? ' (placeholder URL)'
                  : ''}
              </li>
              <li>
                <a
                  className="scorecard__link"
                  {...documentLinkProps(
                    EVALUATION_TEMPLATE_LINKS.finalReportDocx.href
                  )}
                >
                  {EVALUATION_TEMPLATE_LINKS.finalReportDocx.label}
                </a>
                {isPlaceholderLink(
                  EVALUATION_TEMPLATE_LINKS.finalReportDocx.href
                )
                  ? ' (placeholder URL)'
                  : ''}
              </li>
            </ul>
            <label className="bj-field">
              <span className="bj-field__label">
                Upload completed evaluation report (pdf/docx/pptx)
              </span>
              <FileDrop
                accept={FILE_ACCEPT}
                hint=".pdf, .docx or .pptx, up to 10 MB"
                fileName={evaluationReportFileName}
                disabled={isSubmitting}
                onChange={(event) => {
                  const file = event.target.files?.[0] ?? null;
                  setEvaluationReportFile(file);
                  setEvaluationReportFileName(file ? file.name : null);
                }}
              />
            </label>
            {attemptedComplete && !evaluationReportFile ? (
              <p className="bj-field__error">
                Upload the evaluation report before completing Evaluation.
              </p>
            ) : null}
          </div>

          <div className="phase-gate">
            <p className="phase-gate__title">3. Summary banner</p>
            <p>
              Reminder: ensure your success criteria results, evaluation report,
              warm-up message, recap meeting plan, and outcome decision are
              complete before you finish this phase.
            </p>
          </div>

          <div className="phase-gate">
            <p className="phase-gate__title">4. Warm-up communication</p>
            <p>
              Draft a short message summarising PoC status and findings to share
              internally with stakeholders. (Stored in the browser only — not
              sent to the API.)
            </p>
            <label className="bj-field">
              <span className="bj-field__label">Warm-up message</span>
              <textarea
                className="bj-field__input bj-field__input--area"
                value={warmUpMessage}
                disabled={isSubmitting}
                onChange={(event) => setWarmUpMessage(event.target.value)}
              />
            </label>
          </div>

          <div className="phase-gate">
            <p className="phase-gate__title">5. Set up recap meeting</p>
            <p>
              Organise a final review meeting with relevant stakeholders.
              (Optional; browser only.)
            </p>
            <label className="bj-field">
              <span className="bj-field__label">
                Suggested participants and agenda (optional)
              </span>
              <textarea
                className="bj-field__input bj-field__input--area"
                value={recapMeetingNotes}
                disabled={isSubmitting}
                onChange={(event) => setRecapMeetingNotes(event.target.value)}
              />
            </label>
          </div>

          <div className="phase-gate">
            <p className="phase-gate__title">6. Outcome decision point</p>
            <p>
              Was the PoC successful, and do you wish to proceed with
              implementation?
            </p>

            <div className="bj-choice-list">
              <label className="bj-choice">
                <input
                  type="radio"
                  name="evaluation-outcome"
                  checked={proceedWithImplementation === true}
                  disabled={isSubmitting}
                  onChange={() => {
                    setProceedWithImplementation(true);
                  }}
                />
                <span>
                  <strong>Yes</strong> — proceed with implementation
                </span>
              </label>
              <label className="bj-choice">
                <input
                  type="radio"
                  name="evaluation-outcome"
                  checked={proceedWithImplementation === false}
                  disabled={isSubmitting}
                  onChange={() => {
                    setProceedWithImplementation(false);
                    setSolutionType(null);
                  }}
                />
                <span>
                  <strong>No</strong> — archive the PoC
                </span>
              </label>
            </div>

            {proceedWithImplementation === true ? (
              <div className="bj-choice-list">
                <p className="bj-field__label">Post-evaluation pathway</p>
                <label className="bj-choice">
                  <input
                    type="radio"
                    name="solution-type"
                    checked={solutionType === 'IT'}
                    disabled={isSubmitting}
                    onChange={() => setSolutionType('IT')}
                  />
                  <span>
                    <strong>IT solution</strong> — initiate IT Demand
                  </span>
                </label>
                <label className="bj-choice">
                  <input
                    type="radio"
                    name="solution-type"
                    checked={solutionType === 'NON_DIGITAL'}
                    disabled={isSubmitting}
                    onChange={() => setSolutionType('NON_DIGITAL')}
                  />
                  <span>
                    <strong>Non-digital solution</strong> — PID and Business
                    Case path
                  </span>
                </label>
              </div>
            ) : null}

            <label className="bj-field">
              <span className="bj-field__label">Comment</span>
              <textarea
                className="bj-field__input bj-field__input--area"
                value={outcomeComment}
                disabled={isSubmitting}
                onChange={(event) => setOutcomeComment(event.target.value)}
              />
            </label>

            {outcomePreview && outcomePreview.proceed ? (
              <p className="scorecard__notice" role="status">
                {solutionType === 'IT' ? (
                  <>
                    Next step:{' '}
                    <a
                      className="scorecard__link"
                      {...documentLinkProps(IT_DEMAND_FORM.href)}
                    >
                      {IT_DEMAND_FORM.label}
                    </a>{' '}
                    on the IT Portal.
                  </>
                ) : (
                  'Next step guidance: Prepare PID and Business Case, then procurement and Security & Enterprise Architecture (external processes).'
                )}
              </p>
            ) : null}

            {attemptedComplete && proceedWithImplementation === null ? (
              <p className="bj-field__error">Select Yes or No for the outcome.</p>
            ) : null}
            {attemptedComplete &&
            proceedWithImplementation === true &&
            !solutionType ? (
              <p className="bj-field__error">
                Select IT solution or non-digital solution when proceeding.
              </p>
            ) : null}
          </div>

          {submitError ? (
            <p className="bj-field__error">{submitError}</p>
          ) : null}

          <div className="initiation__actions initiation__actions--wrap">
            <button
              type="button"
              className="initiation__link-back"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              onClick={() =>
                navigate('/pocs/new/running/workspace', { state })
              }
            >
              ← Back to Running workspace
            </button>

            <button
              type="button"
              className="initiation__next initiation__next--secondary"
              onClick={() => navigate('/')}
            >
              Back to home
            </button>

            <button
              type="button"
              className="initiation__next"
              disabled={isSubmitting}
              onClick={handleComplete}
            >
              {isSubmitting
                ? 'Submitting...'
                : 'Complete Evaluation phase →'}
            </button>
          </div>
        </Section>
      </div>
    </div>
  );
};

export default EvaluationPhasePage;
